// Compact Topbar control for switching the active location filter. Hidden
// unless the workspace plan has the multiLocation gate AND there are 2+
// active locations - single-location shops never see this.
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, ChevronDown, MapPin } from 'lucide-react';
import * as Popover from '@radix-ui/react-popover';
import { PLAN_DEFINITIONS } from '@wrapforce/shared/constants';
import { useActiveMembership } from '@/features/session/useActiveMembership';
import { useSession } from '@/features/session/useSession';
import { ALL_LOCATIONS, useLocationStore } from '@/stores/location';
import { cn } from '@/lib/cn';
import { useLocations } from './api';

export function LocationSwitcher() {
  const { t } = useTranslation('locations');
  const { session } = useSession();
  const { membership } = useActiveMembership();
  const workspaceId = membership?.workspaceId ?? null;
  const { activeLocationId, setActiveLocationId, loadForContext } = useLocationStore();
  const { data } = useLocations(workspaceId);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (session?.user.id && workspaceId) loadForContext(session.user.id, workspaceId);
  }, [session?.user.id, workspaceId, loadForContext]);

  const gateOn = membership ? PLAN_DEFINITIONS[membership.plan].gates.multiLocation : false;
  const activeLocations = (data?.items ?? []).filter((l) => l.isActive);

  if (!gateOn || activeLocations.length < 2) return null;

  const current = activeLocationId === ALL_LOCATIONS ? null : activeLocations.find((l) => l.id === activeLocationId);
  const label = current?.name ?? t('switcher.allLocations');

  function handleSelect(id: string) {
    if (session?.user.id && workspaceId) setActiveLocationId(session.user.id, workspaceId, id);
    setOpen(false);
  }

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <button
          type="button"
          className="flex items-center gap-1.5 border border-border px-3 py-1.5 text-[13px] font-medium text-ink hover:bg-surface-2"
        >
          <MapPin size={14} />
          <span className="max-w-[9rem] truncate">{label}</span>
          <ChevronDown size={14} />
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content align="end" sideOffset={6} className="z-50 w-56 border border-border bg-surface p-1.5 shadow-md">
          <button
            type="button"
            onClick={() => handleSelect(ALL_LOCATIONS)}
            className={cn(
              'flex w-full items-center justify-between px-2.5 py-2 text-left text-sm hover:bg-surface-2',
              activeLocationId === ALL_LOCATIONS && 'font-semibold text-accent-ink',
            )}
          >
            {t('switcher.allLocations')}
            {activeLocationId === ALL_LOCATIONS ? <Check size={13} /> : null}
          </button>
          {activeLocations.map((loc) => (
            <button
              key={loc.id}
              type="button"
              onClick={() => handleSelect(loc.id)}
              className={cn(
                'flex w-full items-center justify-between px-2.5 py-2 text-left text-sm hover:bg-surface-2',
                activeLocationId === loc.id && 'font-semibold text-accent-ink',
              )}
            >
              {loc.name}
              {activeLocationId === loc.id ? <Check size={13} /> : null}
            </button>
          ))}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
